import { useCallback, useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  registerServiceWorker,
  subscribeToPush,
  unsubscribeFromPush,
  isStandalone,
  isIOS,
} from "@/lib/push";
import { Inbox, Send, Clock, AlertTriangle, RefreshCw, Bell, BellOff, Smartphone } from "lucide-react";

function Stat({ label, value, hint, accent, testid }) {
  return (
    <div className="border border-[#E5E4E0] bg-white p-6 fade-up" data-testid={testid}>
      <div className="label-mono">{label}</div>
      <div className={`mt-2 font-display text-4xl font-light ${accent || "text-[#1C1B1A]"}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-[#8C8B8A] mt-2">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { settings, reloadSettings } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [latestReport, setLatestReport] = useState(null);
  const [recent, setRecent] = useState([]);
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, r, e] = await Promise.all([
        api.get("/emails/stats"),
        api.get("/reports/latest"),
        api.get("/emails?limit=8"),
      ]);
      setStats(s.data);
      setLatestReport(r.data);
      setRecent(e.data);
    } catch (err) {
      console.error("Dashboard: failed to load stats/report/emails", err?.message || err);
    }
  }, []);

  useEffect(() => {
    load();
    registerServiceWorker().then(async () => {
      if ("serviceWorker" in navigator && "PushManager" in window) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setPushOn(!!sub);
      }
    });
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, [load]);

  const togglePush = async () => {
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
        toast.success("Notifiche disattivate");
      } else {
        await subscribeToPush();
        setPushOn(true);
        toast.success("Notifiche push attivate");
      }
    } catch (e) {
      toast.error(e.message || "Errore notifiche");
    }
  };

  const sendTestPush = async () => {
    try {
      const { data } = await api.post("/push/test");
      if (data.sent === 0) toast.error("Nessuna iscrizione push attiva");
      else toast.success(`Notifica inviata a ${data.sent} dispositivi`);
    } catch (err) {
      console.error("Dashboard: push test failed", err?.message || err);
      toast.error("Errore invio test");
    }
  };

  const pollNow = async () => {
    setBusy(true);
    try {
      await api.post("/emails/poll-now");
      toast.success("Polling avviato in background");
      setTimeout(load, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore polling");
    } finally {
      setBusy(false);
    }
  };

  const generateReportNow = async () => {
    setBusy(true);
    try {
      await api.post("/reports/generate-now");
      toast.success("Resoconto generato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nessuna email per il resoconto");
    } finally {
      setBusy(false);
    }
  };

  const masterOn = settings?.master_enabled;
  const showIosHint = isIOS() && !isStandalone();

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="label-mono">Resoconto in tempo reale</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 font-light tracking-tight">
            {masterOn ? "La segretaria è al lavoro." : "Studio in pausa."}
          </h1>
          <p className="text-sm text-[#5C5B5A] mt-3 max-w-xl">
            {masterOn
              ? "Sta monitorando la posta e rispondendo per conto tuo. Tu controlli quando."
              : "Attiva l\u2019interruttore in alto per riprendere le risposte automatiche."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="poll-now-button"
            variant="outline"
            disabled={busy}
            onClick={pollNow}
            className="border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#F0EFEA] font-mono uppercase text-xs tracking-[0.2em]"
          >
            <RefreshCw size={14} className="mr-2" /> Aggiorna inbox
          </Button>
          <Button
            data-testid="generate-report-button"
            disabled={busy}
            onClick={generateReportNow}
            className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase text-xs tracking-[0.2em]"
          >
            Genera resoconto
          </Button>
        </div>
      </div>

      {/* PUSH BANNER */}
      <div className="border border-[#E5E4E0] bg-white p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div className="flex items-start gap-3">
          <div className="border border-[#E5E4E0] p-2">
            {pushOn ? <Bell size={18} className="text-[#2C4C3B]" /> : <BellOff size={18} className="text-[#8C8B8A]" />}
          </div>
          <div>
            <div className="label-mono">Notifiche push</div>
            <p className="text-sm mt-1 text-[#1C1B1A]">
              {pushOn
                ? "Le notifiche di nuove risposte e resoconti arrivano sul tuo dispositivo."
                : "Attivale per essere avvisato di ogni email gestita e di ogni resoconto."}
            </p>
            {showIosHint && (
              <p className="text-xs text-[#C85A32] mt-2 flex items-center gap-1">
                <Smartphone size={12} /> Su iPhone: tocca &ldquo;Condividi&rdquo; → &ldquo;Aggiungi a Home&rdquo;, poi attiva le notifiche.
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="toggle-push-button"
            onClick={togglePush}
            variant="outline"
            className="border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#F0EFEA] font-mono uppercase text-xs tracking-[0.2em]"
          >
            {pushOn ? "Disattiva" : "Attiva"}
          </Button>
          {pushOn && (
            <Button
              data-testid="test-push-button"
              onClick={sendTestPush}
              className="bg-[#C85A32] hover:bg-[#A64624] text-white font-mono uppercase text-xs tracking-[0.2em]"
            >
              Test
            </Button>
          )}
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <Stat
          testid="stat-today"
          label="Email oggi"
          value={stats?.today ?? "—"}
          hint="Dall'inizio della giornata"
        />
        <Stat
          testid="stat-replied"
          label="Risposte inviate"
          value={stats?.by_status?.replied ?? "—"}
          accent="text-[#2C4C3B]"
          hint="Totale storico"
        />
        <Stat
          testid="stat-drafted"
          label="Bozze in attesa"
          value={stats?.by_status?.drafted ?? "—"}
          accent="text-[#C85A32]"
          hint="Da approvare manualmente"
        />
        <Stat
          testid="stat-failed"
          label="Errori"
          value={stats?.by_status?.failed ?? "—"}
          hint="Da verificare in inbox"
        />
      </div>

      {/* LATEST REPORT + RECENT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 border border-[#E5E4E0] bg-white p-6 fade-up delay-2" data-testid="latest-report-card">
          <div className="label-mono">Ultimo resoconto · 4 ore</div>
          {latestReport ? (
            <>
              <p className="font-mono text-xs text-[#5C5B5A] mt-3">
                {new Date(latestReport.window_start).toLocaleString("it-IT")} →
                <br />
                {new Date(latestReport.window_end).toLocaleString("it-IT")}
              </p>
              <div className="grid grid-cols-2 gap-3 mt-5">
                <div>
                  <div className="label-mono text-[10px]">Ricevute</div>
                  <div className="font-display text-3xl font-light">{latestReport.emails_received}</div>
                </div>
                <div>
                  <div className="label-mono text-[10px]">Risposte</div>
                  <div className="font-display text-3xl font-light text-[#2C4C3B]">{latestReport.emails_replied}</div>
                </div>
              </div>
              <p className="text-sm text-[#1C1B1A] mt-5 leading-relaxed border-t border-[#E5E4E0] pt-4 italic">
                {latestReport.summary_text}
              </p>
            </>
          ) : (
            <p className="text-sm text-[#8C8B8A] mt-3">
              Nessun resoconto disponibile. Verrà generato automaticamente ogni 4 ore.
            </p>
          )}
        </div>

        <div className="lg:col-span-2 border border-[#E5E4E0] bg-white fade-up delay-3" data-testid="recent-emails-card">
          <div className="p-6 border-b border-[#E5E4E0] flex items-center justify-between">
            <div>
              <div className="label-mono">Attività recente</div>
              <h3 className="font-display text-2xl mt-1 font-light">Ultime email gestite</h3>
            </div>
            <Link to="/inbox" data-testid="goto-inbox-link" className="text-xs text-[#2C4C3B] underline underline-offset-4 font-mono uppercase tracking-[0.2em]">
              Vedi tutte
            </Link>
          </div>
          <div className="divide-y divide-[#E5E4E0]">
            {recent.length === 0 && (
              <div className="p-12 text-center text-[#8C8B8A] text-sm">
                Nessuna email ancora. Collega un account e attiva la segretaria.
              </div>
            )}
            {recent.map((e) => (
              <div key={e.id} className="px-6 py-4 hover:bg-[#F0EFEA] transition-colors" data-testid={`recent-email-${e.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs text-[#5C5B5A]">
                      <span className="truncate">{e.from_name || e.from_address}</span>
                      <span>·</span>
                      <span>{new Date(e.received_at).toLocaleString("it-IT")}</span>
                    </div>
                    <div className="mt-1 font-medium text-sm truncate">{e.subject}</div>
                  </div>
                  <StatusBadge status={e.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { text: "In attesa", bg: "bg-[#FBF6E9]", fg: "text-[#8B6B00]" },
    drafted: { text: "Bozza", bg: "bg-[#FDF1EB]", fg: "text-[#C85A32]" },
    replied: { text: "Risposto", bg: "bg-[#EAF2ED]", fg: "text-[#2C4C3B]" },
    skipped: { text: "Saltato", bg: "bg-[#F0EFEA]", fg: "text-[#5C5B5A]" },
    failed: { text: "Errore", bg: "bg-[#FEE2E2]", fg: "text-[#991B1B]" },
  };
  const m = map[status] || map.pending;
  return (
    <span className={`inline-block text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 ${m.bg} ${m.fg}`}>
      {m.text}
    </span>
  );
}
